<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import { v4 as uuid } from 'uuid'
import type { HomePageConfig } from '../../configs/types'
import { theme } from '@/config/brands'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps<{
  module: any
  config: HomePageConfig
  gotoBook: (id: string) => void
}>()

const emit = defineEmits<{
  (e: 'update:bg-url', url: string): void
}>()

const primaryColor = theme.primaryColor
const current = ref(0)
const swiper = ref<Swiper | null>(null)
const swiperRef = ref<HTMLElement | null>(null)
const paginationRef = ref<HTMLElement | null>(null)

const books = computed(() => {
  if (props.module?.resourceList?.records?.length > 10)
    return props.module.resourceList.records.slice(0, 10)

  return props.module?.resourceList?.records || []
})

const currentBook = computed(() => {
  return books.value[current.value]
})

// 更新背景图
function updateBgUrl(url: string) {
  emit('update:bg-url', url)
}

function initSwiper() {
  if (!swiperRef.value)
    return
  swiper.value = new Swiper(swiperRef.value, {
    modules: [Pagination, Autoplay],
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: 'auto', // slide 宽度由 CSS 控制（92%），配合 centeredSlides 居中
    spaceBetween: 12, // 图片之间的间距
    autoplay: {
      delay: 10000,
      disableOnInteraction: false,
    },
    pagination: {
      el: paginationRef.value,
      clickable: true,
    },
    loop: true,
    on: {
      init: (swiperInstance) => {
        current.value = swiperInstance.realIndex
        // 初始化时更新背景图
        const currentBookData = books.value[swiperInstance.realIndex]
        if (currentBookData?.contentCoverUrl)
          updateBgUrl(currentBookData.contentCoverUrl)
      },
      slideChange: async (swiperInstance) => {
        current.value = swiperInstance.realIndex
        // 切换时更新背景图
        const currentBookData = books.value[swiperInstance.realIndex]
        if (currentBookData?.contentCoverUrl)
          updateBgUrl(currentBookData.contentCoverUrl)
      },
      click(swiper: any, event) {
        const clickedSlide = (event.target as Element)?.closest('.swiper-slide')
        if (clickedSlide) {
          if (clickedSlide.classList.contains('swiper-slide-active'))
            handleGotoBook()
          else
            swiper.slideToClickedSlide()
        }
      },
    },
  })
}

function handleGotoBook() {
  if (currentBook.value?.id)
    props.gotoBook(currentBook.value.id)
}

// 监听 module 变化，重新初始化 Swiper
watch(() => props.module, () => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
  // 重置当前索引
  current.value = 0
  nextTick(() => {
    initSwiper()
  })
})

onMounted(() => {
  initSwiper()
})

// 组件卸载时销毁 Swiper
onBeforeUnmount(() => {
  if (swiper.value) {
    swiper.value.destroy(true, true)
    swiper.value = null
  }
})
</script>

<template>
  <div ref="swiperRef" class="banner swiper">
    <div class="swiper-wrapper">
      <div v-for="(book, index) in books" :key="index" class="swiper-slide">
        <div class="slide-inner relative w-full">
          <BizBookCover
            :id="uuid()"
            :src="book.contentCoverUrl || ''"
            :extra="{
              module_name: module.showName,
              module_id: module.id,
              module_type: module.columnType,
              author: book.authorInfo?.author,
              ...book,
            }"
            class="book-cover rounded-8px overflow-hidden"
            :ignore-pictures-appear="true"
          />
          <!-- 底部渐变遮罩 + 书名 -->
          <div class="banner-overlay">
            <p class="banner-title truncate">
              {{ book.contentName }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div ref="paginationRef" class="swiper-pagination banner-dots" />
  </div>
</template>

<style scoped>
.banner {
  position: relative;
}

.swiper-slide {
  width: 92%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* 按容器宽度定高，避免 vw 在桌面端导致错位 */
.slide-inner {
  aspect-ratio: 345 / 190;
}

.book-cover {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

/* 底部渐变遮罩（对照蓝湖 rgba(19,22,29) 渐变） */
.banner-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 48px;
  border-radius: 0 0 8px 8px;
  background: linear-gradient(180deg, rgba(19, 22, 29, 0) 0%, rgba(19, 22, 29, 1) 100%);
  display: flex;
  align-items: flex-end;
  padding: 0 12px 10px;
}

.banner-title {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  max-width: 70%;
}

/* 非活跃slide样式 - 只降低透明度，不缩小 */
.swiper-slide:not(.swiper-slide-active) {
  opacity: 0.6;
}

/* 活跃slide样式 - 完整尺寸 */
.swiper-slide-active {
  opacity: 1;
  z-index: 2;
}

/* 分页器：叠加在 Banner 右下角 */
.banner-dots {
  position: absolute;
  left: auto;
  right: calc(4vw + 12px);
  bottom: 12px;
  width: auto;
  z-index: 3;
  display: flex;
  align-items: center;
}

/* 自定义分页器样式 */
:deep(.swiper-pagination-bullet) {
  width: 4px;
  height: 4px;
  background: #ffffff;
  border-radius: 2px;
  opacity: 0.5;
  margin: 0 4px;
}

:deep(.swiper-pagination-bullet-active) {
  width: 16px;
  height: 4px;
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
  border-radius: 3px;
  opacity: 1;
}
</style>
